// src/data/AwarenessFAQData.js

export const earthDayFAQs = [
  {
    id: 'why-celebrated',
    question: 'Why is Earth Day Celebrated?',
    answer: `
      Earth Day is celebrated every year on April 22 to raise awareness and inspire action for the protection of our planet.
      Here's why it matters:
      <ul>
        <li><strong>Environmental Awareness</strong>: Earth Day highlights issues like climate change, pollution, deforestation, and loss of biodiversity.</li>
        <li><strong>Call to Action</strong>: It encourages individuals, communities, and governments to adopt sustainable practices and policies.</li>
        <li><strong>Education & Engagement</strong>: Schools, organizations, and activists use the day to teach others about environmental challenges and solutions.</li>
        <li><strong>Global Solidarity</strong>: It's a moment for people worldwide to unite in protecting the Earth for future generations.</li>
      </ul>
    `,
  },
  {
    id: 'main-environmental-issues',
    question: 'What are the main environmental issues Earth Day addresses?',
    answer: `
      The main issues Earth Day typically addresses:
      <ul>
        <li><strong>Climate Change</strong>:
          <ul>
            <li>Rising global temperatures due to greenhouse gas emissions</li>
            <li>Extreme weather events like floods, droughts, and wildfires</li>
            <li>Advocacy for renewable energy and reduced fossil fuel dependence</li>
          </ul>
        </li>
        <li><strong>Plastic Pollution</strong>:
          <ul>
            <li>Massive accumulation of single-use plastics in oceans and landfills</li>
            <li>Microplastics entering the food chain and harming wildlife</li>
            <li>Recent topics like “Planet vs. Plastics” emphasize this issue</li>
          </ul>
        </li>
        <li><strong>Deforestation & Habitat Loss</strong>:
          <ul>
            <li>Destruction of forests leading to biodiversity loss</li>
            <li>Impact on indigenous communities and endangered species</li>
            <li>Promotion of tree-planting and ecosystem restoration</li>
          </ul>
        </li>
        <li><strong>Air & Water Pollution</strong>:
          <ul>
            <li>Contamination from industrial waste, vehicle emissions, and chemicals</li>
            <li>Threats to human health and aquatic life</li>
            <li>Push for clean air and water legislation</li>
          </ul>
        </li>
        <li><strong>Biodiversity Decline</strong>:
          <ul>
            <li>Extinction of species due to habitat destruction and climate change</li>
            <li>Conservation efforts to protect flora and fauna</li>
            <li>Themes like “Protect Our Species” have highlighted this concern</li>
          </ul>
        </li>
        <li><strong>Sustainable Living & Waste Management</strong>:
          <ul>
            <li>Encouraging the 3 Rs: Reduce, Reuse, Recycle</li>
            <li>Combatting toxic waste and promoting circular economies</li>
            <li>Educational campaigns on eco-friendly habits</li>
          </ul>
        </li>
      </ul>
    `,
  },
  {
    id: 'when-start',
    question: 'When did it start?',
    answer: `
      It began in 1970 as a grassroots movement in the United States and has since grown into a global event observed by over 1 billion people across 193 countries.
    `,
  },
  {
    id: 'how-evolved',
    question: 'How has Earth Day evolved since its inception?',
    answer: `
      Earth Day has transformed from a US-based environmental teach-in into a global movement that mobilizes over a billion people across 190+ countries. Here's how it's evolved over the decades:
      <ul>
        <li><strong>1970: The Beginning</strong>:
          <ul>
            <li>Initiated by Senator Gaylord Nelson and activist Denis Hayes.</li>
            <li>Sparked by the Santa Barbara oil spill and growing pollution concerns.</li>
            <li>First Earth Day saw 20 million Americans rally for environmental reform.</li>
            <li>Led to the creation of the US Environmental Protection Agency (EPA) and landmark laws like the Clean Air Act and Clean Water Act.</li>
          </ul>
        </li>
        <li><strong>1990: Going Global</strong>:
          <ul>
            <li>Earth Day expanded internationally, with 200 million people in 141 countries participating.</li>
            <li>Boosted global recycling efforts and paved the way for the 1992 UN Earth Summit in Rio de Janeiro.</li>
          </ul>
        </li>
        <li><strong>2000s: Focus on Climate & Clean Energy</strong>:
          <ul>
            <li>Earth Day 2000 emphasized global warming and clean energy.</li>
            <li>The internet helped connect 5,000 environmental groups in 184 countries.</li>
          </ul>
        </li>
        <li><strong>2010–2020: Digital Mobilization & Climate Action</strong>:
          <ul>
            <li>Earth Day 2010 saw nearly 1 billion people take action.</li>
            <li>In 2016, the Paris Agreement was signed on Earth Day by 175 nations, marking a historic climate milestone.</li>
            <li>Earth Day 2020, the 50th anniversary, shifted online due to COVID-19, becoming the largest digital environmental mobilization in history.</li>
          </ul>
        </li>
        <li><strong>Today: Annual Themes & Grassroots Power</strong>:
          <ul>
            <li>Each year features a theme like “Invest in Our Planet” or “Planet vs. Plastics”.</li>
            <li>EarthDay.org coordinates global events, challenges, and educational campaigns.</li>
            <li>The movement now includes citizen science, clean-up drives, political advocacy, and youth activism.</li>
          </ul>
        </li>
      </ul>
    `,
  },
  {
    id: 'key-events',
    question: "What were some key events that shaped Earth Day's impact?",
    answer: `
      Earth Day's impact has been shaped by a series of powerful milestones that turned environmental awareness into global action. Here are some of the key events that helped define its legacy:
      <ul>
        <li><strong>1970: The First Earth Day</strong>:
          <ul>
            <li>Sparked by the Santa Barbara oil spill and Rachel Carson's Silent Spring.</li>
            <li>Hosted by Senator Gaylord Nelson and Denis Hayes.</li>
            <li>Mobilized 20 million Americans, leading to the creation of the EPA and laws like the Clean Air Act and Clean Water Act.</li>
          </ul>
        </li>
        <li><strong>1990: Earth Day Goes Global</strong>:
          <ul>
            <li>Expanded to 200 million people in 141 countries.</li>
            <li>Boosted recycling efforts and set the stage for the 1992 UN Earth Summit in Rio.</li>
          </ul>
        </li>
        <li><strong>1992: UN Earth Summit</strong>:
          <ul>
            <li>Produced the Framework Convention on Climate Change and Convention on Biological Diversity.</li>
            <li>Introduced the Rio Declaration, promoting sustainable development worldwide.</li>
          </ul>
        </li>
        <li><strong>2016: Paris Agreement Signed on Earth Day</strong>:
          <ul>
            <li>175 nations signed the landmark climate agreement on April 22, committing to limit global warming.</li>
          </ul>
        </li>
        <li><strong>2024–2025: Planet vs. Plastics</strong>:
          <ul>
            <li>Earth Day 2024 launched a campaign to reduce plastic production by 60% by 2040.</li>
            <li>Highlighted the urgency of tackling single-use plastics and ocean pollution.</li>
          </ul>
        </li>
        <li><strong>🇮🇳 India's Forest Battles (2025)</strong>:
          <ul>
            <li>Earth Day spotlighted deforestation issues like the Great Nicobar Island Project, threatening nearly 1 million trees.</li>
            <li>Citizen-led movements and digital activism emerged, emphasizing the need to protect biodiversity and indigenous rights.</li>
          </ul>
        </li>
      </ul>
      These events show how Earth Day evolved from a protest into a global environmental movement.
    `,
  },
  {
    id: 'current-campaigns-2024',
    question: 'What are some current Earth Day campaigns for 2024?',
    answer: `
      Here are some standout campaigns and initiatives:
      <ul>
        <li><strong>Planet vs. Plastics – EarthDay.org</strong>:
          <ul>
            <li>Called for a Global Plastics Treaty and a ban on single-use plastics by 2030.</li>
            <li>Highlighted the health risks of microplastics, especially for children, through the Babies vs. Plastics report.</li>
            <li>Advocated for innovative materials and eco-friendly alternatives to petrochemical-based plastics.</li>
          </ul>
        </li>
        <li><strong>Youth Engagement & Art Contests</strong>:
          <ul>
            <li>Poster & Writing Contests in cities like Hayward, CA, encouraged students to express views on sustainability, composting, and clean energy.</li>
            <li>Maryland Transportation Authority's Youth Art Competition featured Earth Day-themed bus illustrations promoting public transportation as a climate solution.</li>
          </ul>
        </li>
        <li><strong>Tree Giveaways & Community Events</strong>:
          <ul>
            <li>Libraries like Abilene South Branch partnered with Neighborhood Forest to distribute free saplings to children.</li>
            <li>Cities such as La Mesa, CA hosted Earth Day fairs showcasing eco-friendly businesses and volunteer opportunities.</li>
          </ul>
        </li>
        <li><strong>Wildlife & Habitat Restoration</strong>:
          <ul>
            <li>Audubon Society organized events across the US focused on bird conservation, native plant sales, and climate education.</li>
          </ul>
        </li>
        <li><strong>National Park Week & Volunteer Drives</strong>:
          <ul>
            <li>Celebrated April 20–28 with free park entry, clean-up events, and educational activities to promote nature appreciation.</li>
          </ul>
        </li>
        <li><strong>Science & Sustainability</strong>:
          <ul>
            <li>LCGC International hosted online events on chromatography's role in environmental research, highlighting how science supports sustainability.</li>
          </ul>
        </li>
      </ul>
    `,
  },
];